import { Flex, List, useSafeArea } from '@banhala/ui-kit';
import React, { useMemo, useRef } from 'react';
import type { ListRef } from '@banhala/ui-kit';
import { Keyboard, StyleSheet, View } from 'react-native';
import { useAtomValue } from 'jotai';
import { useBeautyList } from '../queries/useBeautyList';
import { searchHelperParamsAtom } from '../states/searchHelperParams.atom';
import { BeautyHeader } from './BeautyHeader/BeautyHeader';
import { BeautyList } from './BeautyList/BeautyList';
import { PopularSection } from './PopularSection/PopularSection';
import { SuggestList } from './SuggestList/SuggestList';
import { AdsContainer } from './Ads/AdsContainer';
import { RecentHeader } from './RecentHeader/RecentHeader';
import { RecentKeywordList } from './RecentKeywordList/RecentKeywordList';
import { RecommendGoods } from './RecommendGoods/RecommendGoods';
import type { RecentKeywordItem } from '../SearchHelper.page';
import { SearchType } from 'shared/src/api/enums';
import { useSuggestKeywords } from '../queries/useSuggestKeywords';
import { useRecommendedGoods } from '../queries/useRecommendedGoods';

type Props = {
  recentKeywords: RecentKeywordItem[];
  isEditMode: boolean;
  onChangeEditMode: (enabled: boolean) => void;
  onSelectKeyword: (keyword: string, searchType: SearchType) => Promise<void>;
  onDeleteRecentKeyword: (keyword: string) => Promise<void>;
  onClearRecentKeywords: () => Promise<void>;
  resetScrollTrigger: number;
};

type SearchHelperContentsKeys =
  | 'ads'
  | 'recent-header'
  | 'recent-keyword'
  | 'recommend-goods'
  | 'beauty-header'
  | 'beauty-list'
  | 'popular-section';


export const SearchHelperContents = ({
  recentKeywords,
  isEditMode,
  onChangeEditMode,
  onSelectKeyword,
  onDeleteRecentKeyword,
  onClearRecentKeywords,
  resetScrollTrigger,
}: Props) => {
  const listRef = useRef<ListRef<SearchHelperContentsKeys> | null>(null);
  const { bottom } = useSafeArea();

  const { data: beautyListData } = useBeautyList();

  // EXP1802 B군 + 데이터 있을 때만 "최근 본 검색 상품" 캐로셀 노출. A군은 enabled=false 라 미실행.
  const { data: recommendedGoods } = useRecommendedGoods();
  const hasRecommendGoods =
    (recommendedGoods?.item_list?.length ?? 0) > 0;

  const params = useAtomValue(searchHelperParamsAtom);
  const query = params.query?.trim() ?? '';

  // iOS: suggest 결과가 있을 때만 오버레이 표시, 빈 결과면 기본 화면 유지
  const { data: suggestData } = useSuggestKeywords(query);
  const hasSuggestItems =
    suggestData !== undefined &&
    ((suggestData.linked_suggesters?.length ?? 0) > 0 ||
      (suggestData.query_suggesters?.length ?? 0) > 0);
  const showSuggest = query.length > 0 && hasSuggestItems;

  const contentList = useMemo<SearchHelperContentsKeys[]>(() => {
    return [
      'ads',
      ...(recentKeywords.length > 0 ? ['recent-header', 'recent-keyword'] : []),
      ...(hasRecommendGoods ? ['recommend-goods'] : []),
      'beauty-header',
      'beauty-list',
      'popular-section',
    ] as SearchHelperContentsKeys[];
  }, [recentKeywords, hasRecommendGoods]);

  return (
    <Flex flex={1}>
      <List<SearchHelperContentsKeys>
        ref={listRef}
        data={contentList}
        style={{ flex: 1 }}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: bottom }}
        onScrollBeginDrag={Keyboard.dismiss}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item }) => {
          switch (item) {
            case 'ads':
              return <AdsContainer />;
            case 'recent-header':
              return (
                <RecentHeader
                  isEditMode={isEditMode}
                  onEdit={() => onChangeEditMode(true)}
                  onClose={() => onChangeEditMode(false)}
                  onClearAll={onClearRecentKeywords}
                />
              );
            case 'recent-keyword':
              return (
                <RecentKeywordList
                  keywords={recentKeywords}
                  isEditMode={isEditMode}
                  onDelete={onDeleteRecentKeyword}
                  onPress={keyword =>
                    onSelectKeyword(keyword, SearchType.RECENT)
                  }
                  resetScrollTrigger={resetScrollTrigger}
                />
              );
            case 'recommend-goods':
              return <RecommendGoods />;
            case 'beauty-header':
              return <BeautyHeader title={beautyListData.title} />;
            case 'beauty-list':
              return (
                <BeautyList
                  item_list={beautyListData.item_list}
                  onPress={item => {
                    if (item.query)
                      onSelectKeyword(item.query, SearchType.BEAUTY_RECOMMEND);
                  }}
                />
              );
            case 'popular-section':
              return (
                <PopularSection onSelectKeyword={onSelectKeyword} />
              );
            default:
              return null;
          }
        }}
      />
      {showSuggest && (
        <View style={contentsStyles.suggestOverlay}>
          <SuggestList
            query={query}
            data={suggestData}
            onSelect={async item => {
              if (item.type === 'QUERY') {
                await onSelectKeyword(
                  item.suggest,
                  SearchType.AUTO_COMPLETE_SEARCH_KEYWORD,
                );
              }
            }}
          />
        </View>
      )}
    </Flex>
  );
};

const contentsStyles = StyleSheet.create({
  suggestOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'white',
  },
});
